import type {
  BidAssociation,
  Classification,
  Classifications,
  Commitment,
  ScopeItemInput
} from "design-scope";
import type {
  DesignScopeCreateInstance,
  DesignScopeInstanceFields,
  DesignScopeUpdateInstance
} from "services/design-scope-apis";
import { mergeBulkClassificationUpdate } from "./bulk-edit-classification-merge";
import { toGraphqlClassificationId } from "./classification-catalog";

/** Coerce LOD enum ids to numbers when the catalog uses Int PKs. */
function toLodValueId(id: string): string | number {
  const trimmed = id.trim();
  return /^\d+$/.test(trimmed) ? Number(trimmed) : trimmed;
}

/**
 * Catalog id → `responsible_designer_id`. A blank id clears the designer.
 * Callers that only know the display name still send `responsibility`.
 */
function designerFields(
  input: ScopeItemInput
): Pick<
  DesignScopeInstanceFields,
  "responsibility" | "responsible_designer_id"
> {
  if (input.responsibleDesignerId === undefined) {
    return { responsibility: input.responsibility.trim() };
  }
  const trimmed = input.responsibleDesignerId.trim();
  if (!trimmed) return { responsible_designer_id: null };
  return {
    responsible_designer_id: /^\d+$/.test(trimmed) ? Number(trimmed) : trimmed
  };
}

function mapDesignPackages(
  commitments: Commitment[],
  alwaysSendMilestone: boolean
): DesignScopeInstanceFields["design_packages"] {
  return commitments.map((c) => {
    const row: DesignScopeInstanceFields["design_packages"][number] = {
      design_package_id: c.designPackageId,
      level_of_detail_value_id: toLodValueId(c.levelOfDetailValueId),
      notes: c.notes.trim() ? c.notes.trim() : null
    };
    if (alwaysSendMilestone || c.milestoneId) {
      row.design_package_milestone_id = c.milestoneId;
    }
    return row;
  });
}

function mapBidPackages(
  bids: BidAssociation[]
): DesignScopeInstanceFields["bid_packages"] {
  return bids
    .filter((b) => b.bidPackageId)
    .map((b) => {
      const row: DesignScopeInstanceFields["bid_packages"][number] = {
        bid_package_id: b.bidPackageId
      };
      if (b.primary) row.is_primary = true;
      return row;
    });
}

/**
 * Custom label → existing id in `classification_ids`, or new text in
 * `classification[{ title }]`.
 */
function mapCustomClassification(label: Classification | undefined): {
  classification_ids: Array<string | number>;
  classification: DesignScopeInstanceFields["classification"];
} {
  if (!label || label.system !== "CUSTOM") {
    return { classification_ids: [], classification: [] };
  }
  const termId = label.termId?.trim();
  if (termId) {
    return {
      classification_ids: [toGraphqlClassificationId(termId)],
      classification: []
    };
  }
  const title = label.title?.trim();
  return {
    classification_ids: [],
    classification: title ? [{ title }] : []
  };
}

/**
 * Shared modal → API field mapping for create and update.
 * UF/MF/existing-custom term ids → `classification_ids`; new custom text →
 * `classification[{ title }]`. Scope-level notes are not part of the contract
 * (only DP `notes`).
 */
function toDesignScopeInstanceFields(
  input: ScopeItemInput,
  opts?: { alwaysSendMilestone?: boolean }
): DesignScopeInstanceFields {
  const alwaysSendMilestone = opts?.alwaysSendMilestone ?? false;
  const classification_ids: Array<string | number> = [];
  for (const slot of [input.classification?.uf, input.classification?.mf]) {
    const termId = slot?.termId?.trim();
    if (!termId) continue;
    classification_ids.push(toGraphqlClassificationId(termId));
  }

  const custom = mapCustomClassification(input.classification?.label);
  classification_ids.push(...custom.classification_ids);

  const title = input.title?.trim() || null;
  const description = input.description?.trim() || null;

  return {
    title,
    description,
    classification_ids,
    classification: custom.classification,
    ...designerFields(input),
    design_packages: mapDesignPackages(input.commitments, alwaysSendMilestone),
    bid_packages: mapBidPackages(input.bids)
  };
}

/**
 * Map modal `ScopeItemInput` → one createInstances feature row.
 */
export function toDesignScopeCreateInstance(
  input: ScopeItemInput
): DesignScopeCreateInstance {
  return toDesignScopeInstanceFields(input);
}

/**
 * Map modal `ScopeItemInput` + existing id → one update feature row.
 * Always sends `design_package_milestone_id` (null when cleared), matching
 * the update sample. Linking a package/milestone omits classification fields
 * so the existing codes are left unchanged.
 */
export function toDesignScopeUpdateInstance(
  input: ScopeItemInput,
  id: string,
  opts?: { includeClassification?: boolean }
): DesignScopeUpdateInstance {
  const fields = toDesignScopeInstanceFields(input, {
    alwaysSendMilestone: true
  });
  if (opts?.includeClassification === false) {
    const designer =
      fields.responsible_designer_id !== undefined
        ? { responsible_designer_id: fields.responsible_designer_id }
        : { responsibility: fields.responsibility };
    return {
      id,
      title: fields.title,
      description: fields.description,
      ...designer,
      design_packages: fields.design_packages,
      bid_packages: fields.bid_packages
    };
  }
  return { id, ...fields };
}

/**
 * Minimal bulk-link update row: only id + merged design_packages + forwarded
 * bid_packages (so existing bid links are not dropped).
 */
export function toBulkLinkDesignPackageUpdateInstance(
  id: string,
  commitments: Commitment[],
  bids: BidAssociation[]
): DesignScopeUpdateInstance {
  return {
    id,
    design_packages: mapDesignPackages(commitments, true),
    bid_packages: mapBidPackages(bids)
  };
}

/**
 * Minimal bulk-link update row for bid packages: forward existing
 * design_packages and send merged bid_packages.
 */
export function toBulkLinkBidPackageUpdateInstance(
  id: string,
  commitments: Commitment[],
  bids: BidAssociation[]
): DesignScopeUpdateInstance {
  return {
    id,
    design_packages: mapDesignPackages(commitments, true),
    bid_packages: mapBidPackages(bids)
  };
}

/** Options for selective UniFormat / MasterFormat / Custom bulk replace. */
export type BulkEditClassificationReplaceOptions = {
  replaceUf: boolean;
  replaceMf: boolean;
  replaceCustom: boolean;
  /** Row's current classification — forwarded for slots that are not replaced. */
  existing?: Classifications;
};

/**
 * Bulk edit classification: send classification fields and forward existing
 * design/bid packages so those links are not dropped. When replace options are
 * provided, only checked systems come from `classification`; others keep
 * `existing`.
 */
export function toBulkEditClassificationUpdateInstance(
  id: string,
  classification: Classifications | undefined,
  commitments: Commitment[],
  bids: BidAssociation[],
  replace?: BulkEditClassificationReplaceOptions
): DesignScopeUpdateInstance {
  const merged = replace
    ? mergeBulkClassificationUpdate(replace.existing, classification, {
        replaceUf: replace.replaceUf,
        replaceMf: replace.replaceMf,
        replaceCustom: replace.replaceCustom
      })
    : classification;
  const fields = toDesignScopeInstanceFields({
    classification: merged,
    title: "",
    description: "",
    status: "NOT_STARTED",
    responsibility: "Architect",
    notes: "",
    commitments: [],
    bids: []
  });
  return {
    id,
    classification_ids: fields.classification_ids,
    classification: fields.classification,
    design_packages: mapDesignPackages(commitments, true),
    bid_packages: mapBidPackages(bids)
  };
}

/**
 * Bulk edit responsibility: send the designer field and forward existing
 * design/bid packages so those links are not dropped.
 * `designerId` set (including "") sends `responsible_designer_id`; omit it to
 * send the display name as `responsibility`.
 */
export function toBulkEditResponsibilityUpdateInstance(
  id: string,
  responsibility: string,
  commitments: Commitment[],
  bids: BidAssociation[],
  designerId?: string
): DesignScopeUpdateInstance {
  const designer = designerFields({
    title: "",
    description: "",
    status: "NOT_STARTED",
    responsibility,
    responsibleDesignerId: designerId,
    notes: "",
    commitments: [],
    bids: []
  });
  return {
    id,
    ...designer,
    design_packages: mapDesignPackages(commitments, true),
    bid_packages: mapBidPackages(bids)
  };
}

/** Which package sides to clear during bulk unlink. */
export type BulkUnlinkPackageOptions = {
  unlinkDesignPackages: boolean;
  unlinkBidPackages: boolean;
};

/**
 * Bulk unlink: clear selected package sides. The untouched side is forwarded
 * so existing links are not dropped (same contract as bulk-link updates).
 */
export function toBulkUnlinkPackagesUpdateInstance(
  id: string,
  options: BulkUnlinkPackageOptions,
  commitments: Commitment[] = [],
  bids: BidAssociation[] = []
): DesignScopeUpdateInstance {
  return {
    id,
    design_packages: options.unlinkDesignPackages
      ? []
      : mapDesignPackages(commitments, true),
    bid_packages: options.unlinkBidPackages ? [] : mapBidPackages(bids)
  };
}
